/**
 * Veloce WEBConnect XML interface over a raw TCP socket.
 *
 * Used by the ISF agent when the company runs `isfAgentPolling` with
 * `pos = veloce`: the sale is pushed from the restaurant LAN, no tunnel.
 * Mirrors ishopfood-api `VelocePos::tcpRequest`: the payload is written as
 * ISO-8859-1, the answer is read until Veloce closes the socket (or sends
 * EOT, 0x04, or the closing VeloceResponse tag), '#' characters are dropped
 * and the EOT is trimmed.
 */
const EOT = '\x04';
const DEFAULT_TIMEOUT_MS = 10000;
const MAX_ITEM_LIST_PAGES = 500;

export function checkConnexion({config}) {
    return tcpRequest({config, payload: '<?xml version="1.0"?><VeloceRequest><Oper>3</Oper></VeloceRequest>'})
}

export function sendSale({config, payload}) {
    return tcpRequest({config, payload})
}

/* "<Result>4</Result>" -> 4, null when the answer has no Result tag */
export function parseResult(response) {
    if (typeof response !== 'string') {
        return null;
    }
    let match = response.match(/<\s*Result\s*>\s*(-?\d+)\s*<\s*\/\s*Result\s*>/i);
    return match ? parseInt(match[1], 10) : null;
}

export function parseResultMsg(response) {
    if (typeof response !== 'string') {
        return null;
    }
    let match = response.match(/<\s*ResultMsg\s*>([\s\S]*?)<\s*\/\s*ResultMsg\s*>/i);
    return match ? match[1].trim() : null;
}

/* Product numbers of an Oper 1 order, in order of appearance, without duplicates */
export function parseRequestedProducts(payload) {
    let products = [];
    let re = /<\s*Product\s*>\s*(\d+)\s*<\s*\/\s*Product\s*>/gi;
    let match;
    while ((match = re.exec(String(payload))) !== null) {
        if (!products.includes(match[1])) {
            products.push(match[1]);
        }
    }
    return products;
}

/* Oper 5: the POS item list. Paged: the answer carries <Continue>No</Continue> when complete,
   otherwise the <StartFrom> value of the next request. Resolves to the item numbers as strings. */
export async function fetchItemNumbers({config}) {
    let numbers = [];
    let startFrom = 1;
    for (let page = 0; page < MAX_ITEM_LIST_PAGES; page++) {
        let payload = '<?xml version="1.0"?><VeloceRequest><Oper>5</Oper><StartFrom>' + startFrom + '</StartFrom></VeloceRequest>';
        let response = await tcpRequest({config, payload});
        let result = parseResult(response);
        if (result === 0) {
            break; /* no item from this position on */
        }
        if (result !== 1) {
            throw {name: 'VeloceError', message: 'item list (Oper 5) answered Result ' + result + ' ' + (parseResultMsg(response) || '')};
        }
        let itemRe = /<\s*Item\s*>([\s\S]*?)<\s*\/\s*Item\s*>/gi;
        let item;
        while ((item = itemRe.exec(response)) !== null) {
            let number = item[1].match(/<\s*Number\s*>\s*(\d+)\s*<\s*\/\s*Number\s*>/i);
            if (number && !numbers.includes(number[1])) {
                numbers.push(number[1]);
            }
        }
        let next = response.match(/<\s*Continue\s*>\s*([^<]*?)\s*<\s*\/\s*Continue\s*>/i);
        if (!next || /^no$/i.test(next[1]) || !/^\d+$/.test(next[1]) || parseInt(next[1], 10) <= startFrom) {
            break;
        }
        startFrom = parseInt(next[1], 10);
    }
    return numbers;
}

/* After a Result 4 ("at least one product does not exist"): which products of the order Veloce does not know.
   An empty item list is treated as an error, so a POS without the menu module never flags every product. */
export async function findMissingProducts({config, payload}) {
    let requested = parseRequestedProducts(payload);
    let known = await fetchItemNumbers({config});
    if (known.length === 0) {
        throw {name: 'VeloceError', message: 'item list (Oper 5) is empty'};
    }
    return requested.filter(product => !known.includes(product));
}

function tcpRequest({payload, config}) {
    if (typeof nw === 'undefined' || !nw) {
        return Promise.reject({name: 'VeloceError', message: 'no access to nw'})
    }

    /* function to debug, console.log when config.debug === true */
    let log = config.debug ? (...str) => console.log(...str) : () => {}
    let net = nw.require('net');
    let endpoint = config.host + ':' + config.port;
    let timeoutMs = config.timeout || DEFAULT_TIMEOUT_MS;

    log('veloce request', endpoint, payload)
    return new Promise((resolve, reject) => {
        let client = new net.Socket();
        let chunks = [];
        let error = null;
        let settled = false;

        let finish = () => {
            if (settled) {
                return;
            }
            settled = true;
            let response = Buffer.concat(chunks).toString('latin1')
                .replace(/#/g, '')
                .replace(/\x04/g, '')
                .trim();
            if (response) {
                log('veloce response', response)
                resolve(response)
            } else {
                reject(error || {name: 'VeloceError', message: 'empty response from ' + endpoint})
            }
        };

        client.setTimeout(timeoutMs);

        client.on('timeout', () => {
            error = {name: 'VeloceTimeout', message: 'no answer from ' + endpoint + ' after ' + timeoutMs + ' ms'};
            log('veloce timeout', endpoint)
            client.destroy()
        });

        client.on('data', (data) => {
            chunks.push(data);
            let soFar = Buffer.concat(chunks).toString('latin1');
            log('veloce data', data.toString('latin1'))
            if (soFar.includes(EOT) || /<\s*\/\s*VeloceResponse\s*>/i.test(soFar)) {
                client.end()
            }
        });

        client.on('error', (err) => {
            error = {name: (err && err.code) || 'VeloceError', message: (err && err.message) || String(err)}
            log('veloce error', err)
        });

        client.on('close', () => {
            log('veloce close')
            finish()
        });

        client.connect(parseInt(config.port, 10), config.host, () => {
            client.write(Buffer.from(String(payload).trim(), 'latin1'))
        });
    })
}
